import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { Municipio, MunicipioSearchResponse, MunicipioFiltro } from '../../../domain/model/municipio/municipio';
import { MunicipioRepository } from '../../../domain/service/municipio/municipio-repository';

/**
 * Adaptador HTTP: HttpMunicipioRepository
 *
 * Implementa el puerto MunicipioRepository para comunicación HTTP con backend.
 *
 * Endpoints:
 * - GET /api/municipios/search?codigo=&codigoSura=&nombre=&page=&size=
 * - GET /api/municipios/{codigo}
 *
 * @author Emerson Rodriguez - Ingeneo
 */
@Injectable({ providedIn: 'root' })
export class HttpMunicipioRepository implements MunicipioRepository {

  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.backend.baseUrl}/servicios/municipios`;
  private static readonly PAGE_SIZE_MIN = 1;
  private static readonly PAGE_SIZE_MAX = 100;

  /**
   * Busca municipios con filtros y paginación por cursor.
   */
  search(
    filtro: MunicipioFiltro,
    cursor: string | null,
    size = 10,
    direction: 'NEXT' | 'PREVIOUS' | 'LAST' = 'NEXT'
  ): Promise<MunicipioSearchResponse> {
    this.validarPaginacion(size);

    let params = new HttpParams()
      .set('size', size.toString())
      .set('direction', direction);

    if (cursor) {
      params = params.set('cursor', cursor);
    }
    if (filtro.codigo?.trim()) {
      params = params.set('codigo', filtro.codigo.trim());
    }
    if (filtro.codigoSura?.trim()) {
      params = params.set('codigoSura', filtro.codigoSura.trim());
    }
    if (filtro.nombre?.trim()) {
      params = params.set('nombre', filtro.nombre.trim());
    }

    return firstValueFrom(
      this.http.get<MunicipioSearchResponse>(`${this.baseUrl}/search`, { params })
    );
  }

  /**
   * Obtiene un municipio por código DANE
   */
  getByCodigo(codigo: string): Promise<Municipio> {
    return firstValueFrom(
      this.http.get<Municipio>(`${this.baseUrl}/${codigo}`)
    );
  }

  /**
   * Obtiene municipios de un departamento
   */
  getByDepartamento(cddepartamento: string): Promise<Municipio[]> {
    return firstValueFrom(
      this.http.get<Municipio[]>(`${this.baseUrl}/departamento/${cddepartamento}`)
    );
  }

  private validarPaginacion(size: number): void {
    if (!Number.isInteger(size) || size < HttpMunicipioRepository.PAGE_SIZE_MIN || size > HttpMunicipioRepository.PAGE_SIZE_MAX) {
      throw new Error(
        `size debe estar entre ${HttpMunicipioRepository.PAGE_SIZE_MIN} y ${HttpMunicipioRepository.PAGE_SIZE_MAX}.`
      );
    }
  }
}
